// <musa-conversion>: capa de conversion de Musa (WhatsApp, talla obligatoria, barra fija de compra,
// barra de confianza, ayuda de ancho desplegable, envio gratis y nota de envio fija). Mejora el DOM que
// ya renderizan las plantillas sin editarlas, asi que cada paso es idempotente y se reaplica tras los
// repintados parciales del tema.

const SVG = 'viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';
const TRUST_ICONS = [
  `<svg ${SVG}><rect x="2.5" y="6" width="19" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/></svg>`,
  `<svg ${SVG}><path d="M2.5 6.5h11v9h-11z"/><path d="M13.5 9.5h4l3 3v3h-7"/><circle cx="6.5" cy="17.5" r="1.5"/><circle cx="17" cy="17.5" r="1.5"/></svg>`,
  `<svg ${SVG}><path d="M3.5 12h17"/><path d="M7 8.5 3.5 12 7 15.5"/><path d="M17 8.5l3.5 3.5-3.5 3.5"/></svg>`,
  `<svg ${SVG}><path d="M4 8.5h13l-3-3"/><path d="M20 15.5H7l3 3"/></svg>`
];
const CHAT_ICON = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20.5 11.5a8.5 8.5 0 0 1-12.4 7.6L3.5 20.5l1.4-4.4a8.5 8.5 0 1 1 15.6-4.6z"/></svg>';

class MusaConversion extends HTMLElement {
  #ac;
  #cfg;
  #observer;
  #io;
  #pending = false;
  #sizeChosen = false;
  #gateActive = false;
  #helpOpen = false;
  #atc = null;
  #atcVisible = true;
  #bar = null;
  #cart = null;
  #cartKey = null;
  #cartFetch = null;

  connectedCallback() {
    try {
      this.#cfg = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || 'null');
    } catch {
      return;
    }
    if (!this.#cfg) return;
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    // A variant in the URL means the buyer already picked a size (shared link or reload).
    this.#sizeChosen = new URL(location.href).searchParams.has('variant');
    document.addEventListener('click', (event) => this.#onClick(event), { capture: true, signal });
    document.addEventListener('change', (event) => this.#onChange(event), { capture: true, signal });
    // Capture phase runs before s-product-form's own submit listener, so a missing size never reaches the cart.
    document.addEventListener('submit', (event) => this.#onSubmit(event), { capture: true, signal });
    this.#observer = new MutationObserver(() => this.#schedule());
    this.#observer.observe(document.body, { childList: true, subtree: true });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#io?.disconnect();
    this.#cartFetch?.abort();
  }

  // A microtask (not requestAnimationFrame) so enhancements land before paint and also run in background tabs.
  #schedule() {
    if (this.#pending) return;
    this.#pending = true;
    queueMicrotask(() => {
      this.#pending = false;
      this.#enhance();
    });
  }

  #enhance() {
    const template = this.#cfg.template;
    if (template === 'product') this.#enhanceProduct();
    if (template === 'index') this.#enhanceHome();
    if (this.#cfg.shipping?.enabled) this.#enhanceCarts();
  }

  // ---------- Product page ----------

  #productForm() {
    return document.querySelector('[data-product-form]');
  }

  #sizeRadios(productForm) {
    const name = this.#cfg.size?.option;
    if (!name || !productForm) return [];
    return [...productForm.querySelectorAll('s-variant-picker input[type="radio"]')].filter((radio) => radio.name === name);
  }

  #enhanceProduct() {
    const productForm = this.#productForm();
    if (!productForm) return;
    const form = productForm.querySelector('form');
    const radios = this.#sizeRadios(productForm);
    this.#gateActive = Boolean(this.#cfg.size?.enabled) && radios.length > 1 && !this.#sizeChosen;
    this.#syncSizeState(radios);
    this.#collapseHelp(productForm);
    const atc = form?.querySelector('button[name="add"]') || null;
    this.#syncAddButton(atc);
    this.#syncWallet(form);
    this.#ensurePdpExtras(productForm, form);
    this.#syncHelpLinks();
    if (this.#cfg.sticky) this.#syncSticky(atc);
  }

  #syncSizeState(radios) {
    const fieldset = radios[0]?.closest('fieldset');
    if (!fieldset) return;
    const label = fieldset.querySelector('[data-selected-value-label]');
    const prompt = this.#cfg.size.prompt;
    if (this.#gateActive) {
      if (!fieldset.hasAttribute('data-musa-unset')) fieldset.setAttribute('data-musa-unset', '');
      if (label && label.textContent.trim() !== prompt) label.textContent = ` ${prompt}`;
      return;
    }
    if (fieldset.hasAttribute('data-musa-unset')) fieldset.removeAttribute('data-musa-unset');
    const checked = radios.find((radio) => radio.checked);
    const value = (checked?.dataset.valueLabel || checked?.value || '').trim();
    if (label && value && label.textContent.trim() === prompt) label.textContent = ` ${value}`;
    fieldset.parentElement?.querySelector('[data-musa-size-error]')?.remove();
  }

  // Turns the picker's long help boxes ("¿Ancho normal o recio?") into a closed disclosure so the buy button sits higher.
  #collapseHelp(productForm) {
    if (!this.#cfg.collapseHelp) return;
    const picker = productForm.querySelector('s-variant-picker');
    if (!picker) return;
    for (const box of picker.querySelectorAll(':scope > div.rounded-card')) {
      const title = box.querySelector('p');
      if (!title || box.childElementCount < 2) continue;
      const details = document.createElement('details');
      details.className = box.className;
      details.setAttribute('data-musa-collapsible', '');
      details.open = this.#helpOpen;
      const summary = document.createElement('summary');
      summary.textContent = title.textContent.trim();
      details.append(summary);
      for (const child of [...box.children]) if (child !== title) details.append(child);
      details.addEventListener('toggle', () => {
        this.#helpOpen = details.open;
      }, { signal: this.#ac.signal });
      box.replaceWith(details);
    }
  }

  #syncAddButton(atc) {
    if (!atc || atc.disabled) return;
    if (!atc.dataset.musaLabel) atc.dataset.musaLabel = atc.textContent.trim();
    const wanted = this.#gateActive ? this.#cfg.size.prompt : atc.dataset.musaLabel;
    if (atc.textContent.trim() !== wanted) atc.textContent = wanted;
  }

  #syncWallet(form) {
    // Express checkout buys the hidden default variant, so it waits until a size is chosen.
    const wallet = form?.querySelector('.shopify-payment-button')?.parentElement;
    if (!wallet) return;
    if (wallet.inert !== this.#gateActive) wallet.inert = this.#gateActive;
    if (wallet.hasAttribute('data-musa-gated') !== this.#gateActive) wallet.toggleAttribute('data-musa-gated', this.#gateActive);
  }

  #ensurePdpExtras(productForm, form) {
    if (!form || productForm.querySelector('[data-musa-pdp]')) return;
    const wrap = document.createElement('div');
    wrap.setAttribute('data-musa-pdp', '');
    const whatsapp = this.#cfg.whatsapp;
    if (whatsapp?.enabled && whatsapp.number && whatsapp.pdpButton) {
      const link = document.createElement('a');
      link.setAttribute('data-musa-wa', 'product');
      link.href = this.#waUrl(this.#productMessage(whatsapp.product));
      link.target = '_blank';
      link.rel = 'noopener';
      link.className = 'inline-flex items-center justify-center gap-2 font-medium min-h-13 px-4 text-base rounded-button w-full';
      link.innerHTML = CHAT_ICON;
      link.append(document.createTextNode(whatsapp.productLabel));
      wrap.append(link);
    }
    if (this.#cfg.trust?.enabled) wrap.append(this.#trustList('compact'));
    if (wrap.childElementCount) form.after(wrap);
  }

  #syncHelpLinks() {
    const whatsapp = this.#cfg.whatsapp;
    if (!whatsapp?.enabled || !whatsapp.number || !whatsapp.fixHelp) return;
    for (const link of document.querySelectorAll('main a[href="/"]:not([data-musa-wa])')) {
      if (!/escr[ií]benos/i.test(link.textContent)) continue;
      link.setAttribute('data-musa-wa', 'size-help');
      link.href = this.#waUrl(this.#productMessage(whatsapp.sizeHelp));
      link.target = '_blank';
      link.rel = 'noopener';
    }
  }

  #syncSticky(atc) {
    if (!atc) return;
    if (!this.#bar) this.#bar = this.#buildBar();
    if (!this.#bar.isConnected) document.body.append(this.#bar);
    if (atc !== this.#atc) {
      if (!this.#io) {
        this.#io = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.target !== this.#atc) continue;
            this.#atcVisible = entry.isIntersecting;
            this.#toggleBar();
          }
        });
      }
      if (this.#atc) this.#io.unobserve(this.#atc);
      this.#atc = atc;
      this.#io.observe(atc);
    }
    this.#updateBar();
  }

  #buildBar() {
    const bar = document.createElement('div');
    bar.setAttribute('data-musa-sticky-bar', '');
    bar.inert = true;
    bar.innerHTML = '<div data-musa-sticky-info><p data-musa-sticky-title></p><p data-musa-sticky-price></p></div>';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'inline-flex items-center justify-center gap-2 font-medium button-primary min-h-12 px-4 text-base rounded-button shrink-0';
    button.setAttribute('data-musa-sticky-button', '');
    button.addEventListener('click', () => {
      if (this.#gateActive) {
        this.#promptSize();
        return;
      }
      this.#atc?.click();
    }, { signal: this.#ac.signal });
    bar.append(button);
    return bar;
  }

  #toggleBar() {
    const show = !this.#atcVisible;
    document.documentElement.toggleAttribute('data-musa-sticky', show);
    if (this.#bar) this.#bar.inert = !show;
  }

  #updateBar() {
    const bar = this.#bar;
    if (!bar) return;
    this.#setText(bar.querySelector('[data-musa-sticky-title]'), this.#shortTitle());
    this.#setText(bar.querySelector('[data-musa-sticky-price]'), document.querySelector('[data-product-price] bdi')?.textContent.trim() || '');
    const button = bar.querySelector('[data-musa-sticky-button]');
    const label = this.#gateActive ? this.#cfg.size.prompt : (this.#atc?.dataset.musaLabel || this.#atc?.textContent.trim() || '');
    this.#setText(button, label);
    const disabled = Boolean(this.#atc?.disabled);
    if (button.disabled !== disabled) button.disabled = disabled;
  }

  #shortTitle() {
    const title = this.#cfg.product?.title || document.querySelector('h1')?.textContent.trim() || '';
    const parts = title.split(' — ');
    return parts.length > 1 ? parts[parts.length - 1] : title;
  }

  #promptSize() {
    const radios = this.#sizeRadios(this.#productForm());
    const fieldset = radios[0]?.closest('fieldset');
    if (!fieldset) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    fieldset.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    fieldset.removeAttribute('data-musa-attention');
    void fieldset.offsetWidth;
    fieldset.setAttribute('data-musa-attention', '');
    let error = fieldset.parentElement?.querySelector('[data-musa-size-error]');
    if (!error) {
      error = document.createElement('p');
      error.setAttribute('data-musa-size-error', '');
      error.setAttribute('role', 'alert');
      fieldset.after(error);
    }
    error.textContent = this.#cfg.size.error;
  }

  #isSizeControl(target) {
    const name = this.#cfg.size?.option;
    if (!name || !(target instanceof Element)) return false;
    const input = target instanceof HTMLInputElement ? target : target.closest('label')?.querySelector('input[type="radio"]');
    return Boolean(input && input.name === name && input.closest('s-variant-picker'));
  }

  #onChange(event) {
    if (this.#cfg.template !== 'product' || !event.isTrusted || !this.#isSizeControl(event.target)) return;
    this.#sizeChosen = true;
    this.#schedule();
  }

  #onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // Clicking the size that is already checked fires no change event, so the click itself counts as a choice.
    if (this.#cfg.template === 'product' && event.isTrusted && this.#isSizeControl(target)) {
      this.#sizeChosen = true;
      this.#schedule();
    }
    const link = target.closest('[data-musa-wa]');
    if (link instanceof HTMLAnchorElement) this.#onWhatsApp(link);
  }

  #onSubmit(event) {
    if (!this.#gateActive) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('[data-product-form]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#promptSize();
  }

  // ---------- WhatsApp ----------

  #onWhatsApp(link) {
    const kind = link.getAttribute('data-musa-wa');
    const whatsapp = this.#cfg.whatsapp;
    if (!whatsapp?.number) return;
    const onProduct = this.#cfg.template === 'product' && Boolean(this.#cfg.product);
    let message = whatsapp.general;
    if (onProduct && kind === 'size-help') message = this.#productMessage(whatsapp.sizeHelp);
    else if (onProduct && (kind === 'product' || kind === 'float')) message = this.#productMessage(whatsapp.product);
    link.href = this.#waUrl(message);
    this.#track(kind);
  }

  #productMessage(template) {
    const product = this.#cfg.product || {};
    const productForm = this.#productForm();
    const title = product.title || document.querySelector('h1')?.textContent.trim() || '';
    const variantId = productForm?.querySelector('input[name="id"]')?.value;
    const variants = product.variants || [];
    const sku = variants.find((variant) => String(variant.id) === String(variantId))?.sku || variants[0]?.sku || '';
    // The sales bot reads the model code as the first SKU segment (S0542 in S0542-PLA-37-N).
    const code = String(sku).split(/[-_ ]/)[0];
    const model = this.#shortTitle().replace(/^modelo\s+/i, '');
    const options = new Map();
    for (const radio of productForm?.querySelectorAll('s-variant-picker input[type="radio"]') || []) {
      if (!options.has(radio.name)) options.set(radio.name, '');
      if (radio.checked) options.set(radio.name, (radio.dataset.valueLabel || radio.value).trim());
    }
    const pending = this.#cfg.size?.pending || '';
    if (this.#gateActive && options.has(this.#cfg.size.option)) options.set(this.#cfg.size.option, pending);
    const summary = [...options].map(([name, value]) => `${name.toLowerCase()} ${(value || pending).toLowerCase()}`).join(', ');
    return String(template || '')
      .replaceAll('{modelo}', model)
      .replaceAll('{producto}', title)
      .replaceAll('{codigo}', code)
      .replaceAll('{opciones}', summary)
      .replaceAll('{url}', product.url || location.href.split('?')[0])
      .replace(/ {2,}/g, ' ')
      .trim();
  }

  #waUrl(message) {
    return `https://wa.me/${this.#cfg.whatsapp.number}?text=${encodeURIComponent(message || '')}`;
  }

  #track(source) {
    // Gives the Meta campaign a measurable signal for sales that close on WhatsApp.
    try {
      window.fbq?.('track', 'Contact', { content_category: 'whatsapp', content_name: source || 'whatsapp' });
    } catch {}
    try {
      window.Shopify?.analytics?.publish?.('musa_whatsapp_click', { source, template: this.#cfg.template });
    } catch {}
  }

  // ---------- Trust bar ----------

  #trustList(variant) {
    const list = document.createElement('ul');
    list.setAttribute('data-musa-trust', variant);
    (this.#cfg.trust?.items || []).forEach((text, index) => {
      if (!text) return;
      const item = document.createElement('li');
      item.innerHTML = TRUST_ICONS[index] || TRUST_ICONS[0];
      const span = document.createElement('span');
      span.textContent = text;
      item.append(span);
      list.append(item);
    });
    return list;
  }

  #enhanceHome() {
    if (!this.#cfg.trust?.enabled || document.querySelector('[data-musa-home-trust]')) return;
    const hero = document.querySelector('main section');
    if (!hero) return;
    const wrap = document.createElement('div');
    wrap.setAttribute('data-musa-home-trust', '');
    wrap.append(this.#trustList('wide'));
    hero.after(wrap);
  }

  // ---------- Cart: free shipping progress and shipping note ----------

  #enhanceCarts() {
    const forms = [...document.querySelectorAll('form')].filter((form) => form.querySelector('[name="checkout"]'));
    if (!forms.length) return;
    let key = '';
    for (const form of forms) {
      if (!form.querySelector('[data-musa-ship]')) form.prepend(this.#buildShip());
      this.#ensureShipNote(form);
      key += `${form.querySelector('[data-cart-total]')?.textContent.trim() || ''}|`;
    }
    if (this.#cart) this.#paintShip();
    if (key !== this.#cartKey) {
      this.#cartKey = key;
      this.#refreshCart();
    }
  }

  // Musa charges one flat rate everywhere, so the theme's "calculated by address" note becomes the real price.
  #ensureShipNote(form) {
    const shipping = this.#cfg.shipping;
    if (!shipping?.notePaid || form.querySelector('[data-musa-ship-note]')) return;
    const note = form.querySelector('[name="checkout"] ~ p');
    if (!note) return;
    note.setAttribute('data-musa-ship-note', '');
    note.textContent = this.#cart ? this.#shipNoteText(this.#cart) : '';
    if (shipping.payNote) {
      const pay = document.createElement('p');
      pay.className = note.className;
      pay.setAttribute('data-musa-pay-note', '');
      pay.textContent = shipping.payNote;
      note.after(pay);
    }
  }

  #shipNoteText(cart) {
    const shipping = this.#cfg.shipping;
    const threshold = Math.round(Number(shipping.threshold || 0) * 100);
    const free = threshold > 0 && cart.total_price >= threshold;
    return free ? shipping.noteFree || shipping.notePaid : shipping.notePaid;
  }

  #buildShip() {
    const element = document.createElement('div');
    element.setAttribute('data-musa-ship', '');
    element.setAttribute('role', 'status');
    element.hidden = !this.#cart?.item_count;
    element.innerHTML = '<p data-musa-ship-text></p><div data-musa-ship-track><div data-musa-ship-fill></div></div>';
    return element;
  }

  async #refreshCart() {
    this.#cartFetch?.abort();
    const controller = new AbortController();
    this.#cartFetch = controller;
    try {
      // `.js` returns the cart as JSON; the base path comes from Liquid routes.
      const response = await fetch(`${this.#cfg.routes.cart}.js`, { signal: controller.signal, headers: { Accept: 'application/json' } });
      this.#cart = await response.json();
      this.#paintShip();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[musa-conversion] cart', error);
    }
  }

  #paintShip() {
    const cart = this.#cart;
    const shipping = this.#cfg.shipping;
    if (!cart) return;
    for (const note of document.querySelectorAll('[data-musa-ship-note]')) this.#setText(note, this.#shipNoteText(cart));
    // Same rule as the sales bot and the Shopify rate: free from the threshold, which two pairs always reach.
    const threshold = Math.round(Number(shipping.threshold || 0) * 100);
    for (const element of document.querySelectorAll('[data-musa-ship]')) {
      const hidden = !cart.item_count || !threshold;
      if (element.hidden !== hidden) element.hidden = hidden;
      if (hidden) continue;
      const remaining = threshold - cart.total_price;
      const done = remaining <= 0;
      let text = shipping.done;
      if (!done) text = cart.item_count === 1 ? shipping.progressPair : String(shipping.progressMoney || '').replaceAll('{monto}', this.#money(remaining));
      this.#setText(element.querySelector('[data-musa-ship-text]'), text);
      if (element.hasAttribute('data-done') !== done) element.toggleAttribute('data-done', done);
      const fill = element.querySelector('[data-musa-ship-fill]');
      const width = `${Math.min(100, Math.round((cart.total_price / threshold) * 100))}%`;
      if (fill && fill.style.width !== width) fill.style.width = width;
    }
  }

  #money(cents) {
    const amount = Math.ceil(cents / 100).toLocaleString('es-HN');
    return String(this.#cfg.money || 'L 0').replace('0', amount);
  }

  #setText(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }
}

customElements.get('musa-conversion') || customElements.define('musa-conversion', MusaConversion);
